import { createReducer, PayloadAction } from '@reduxjs/toolkit';
import { findIndex, pipe, propEq, append, pathOr } from 'ramda';

import {
  ArtworkDynamicData,
  ArtworksState,
  ChangeGridSizePayload,
  FetchArtworksPayload,
  FetchFavoriteArtworksPayload,
  ResponseAddFavoritePayload,
} from '@definitions/artworks.types';

import * as actions from './groupingArtworks.actions';
import { GRID_SIZE } from './groupingArtworks.constants';

export const initialState: ArtworksState = {
  gridSize: GRID_SIZE.MEDIUM,
  artworks: [],
  loading: false,
  favoriteArtworks: [],
  artworkDynamicData: {},
  currentPage: 1,
  meta: { hasNext: false, count: 0, page: 1 },
};

const changeGridSize = (state: ArtworksState, { payload }: PayloadAction<ChangeGridSizePayload>) => {
  state.gridSize = payload.gridSize;
};

const initialFetchArtworks = (state: ArtworksState, data: any) => {
  state.loading = true;
  const infiniteLoader = pathOr(false, ['meta', 'arg', 'infiniteLoader'], data);
  if (!infiniteLoader) {
    state.artworks = [];
  }
};

const fetchArtworks = (state: ArtworksState, { payload }: PayloadAction<FetchArtworksPayload>) => {
  state.meta = payload.meta;
  state.loading = false;

  if (payload.infiniteLoader) {
    state.artworks = state.artworks.concat(payload.artworks);
    return;
  }

  state.artworks = payload.artworks;
};

const fetchFavoriteArtworks = (state: ArtworksState, { payload }: PayloadAction<FetchFavoriteArtworksPayload>) => {
  state.favoriteArtworks = payload.artworks;
};

const setInitialArtworks = (state: ArtworksState, { payload }: PayloadAction<FetchArtworksPayload>) => {
  state.artworks = payload.artworks;
  state.meta = payload.meta;
};

const resetArtworks = (state: ArtworksState) => {
  state.artworks = [];
  state.meta = { hasNext: false, count: 0, page: 1 };
};

const updateKeyBySlug = (key: string, value: boolean) => (
  state: ArtworksState,
  { payload }: PayloadAction<ResponseAddFavoritePayload>
) => {
  const updateArtworksAt = (index: number) => {
    state.artworks[index] = { ...state.artworks[index], [key]: value };
  };

  const updateFavoriteArtworksAt = (index: number) => {
    if (index >= 0) {
      state.favoriteArtworks[index] = { ...state.favoriteArtworks[index], [key]: value };
      return;
    }

    const artworkIndex = findIndex(propEq('slug', payload.slug), state.artworks);
    state.favoriteArtworks = append({ ...state.artworks[artworkIndex], [key]: value }, state.favoriteArtworks);
  };

  pipe(findIndex(propEq('slug', payload.slug)), updateArtworksAt)(state.artworks);

  if (key === 'favorite') {
    pipe(findIndex(propEq('slug', payload.slug)), updateFavoriteArtworksAt)(state.favoriteArtworks);
  }
};

const setArtworkDynamicData = (state: ArtworksState, { payload }: PayloadAction<ArtworkDynamicData>) => {
  state.artworkDynamicData = payload;
};

const setCurrentPage = (state: ArtworksState, { payload }: PayloadAction<number>) => {
  state.currentPage = payload;
};

export const reducer = createReducer(initialState, (builder) => {
  builder.addCase(actions.changeGridSize, changeGridSize);
  builder.addCase(actions.setInitialArtworks, setInitialArtworks);
  builder.addCase(actions.fetchArtworks.pending, initialFetchArtworks);
  builder.addCase(actions.fetchArtworks.fulfilled, fetchArtworks);
  builder.addCase(actions.fetchFavoriteArtworks.fulfilled, fetchFavoriteArtworks);
  builder.addCase(actions.resetArtworks, resetArtworks);
  builder.addCase(actions.setArtworkDynamicData, setArtworkDynamicData);
  builder.addCase(actions.setCurrentPage, setCurrentPage);
  builder.addCase(actions.addToFavorite.fulfilled, updateKeyBySlug('favorite', true));
  builder.addCase(actions.addToWaitlist.fulfilled, updateKeyBySlug('waitlist', true));
  builder.addCase(actions.removeFromFavorite.fulfilled, updateKeyBySlug('favorite', false));
  builder.addCase(actions.removeFromWaitlist.fulfilled, updateKeyBySlug('waitlist', false));
});
